// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMUniqueIdService } from '@app/utils';
import { FormRequestType, ListRequestType, LMDialogButtonKey } from '@app/utils/app.constants';
import { SohoModalDialogRef, SohoModalDialogService } from 'ids-enterprise-ng';
import { LMCreateReportDialogComponent } from '@app/components/report/create-report/create-report-dialog/lm-create-report-dialog.component';
import { Observable, of, pipe, Subject, throwError } from 'rxjs';
import { LMFormRequestService } from '@app/components/form/services/lm-form-request.service';
import { LMFieldPickerService } from '../../../modal/field-picker/services/lm-field-picker-service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LMErrorUtilService, LMUrlUtilService } from '@app/services';
import {
  LMReportPersonalizationDialogResult,
  LMReportPersonalizationDialogResultCode,
  LMReportPersonalizationDialogService,
} from '../lm-report-personalization-dialog.service';
import {
  LMFieldPickerRequest,
  LMFieldPickerResponse,
} from '../../../modal/field-picker/lm-field-picker';
import {
  catchError,
  first,
  map,
  take,
  takeUntil,
  timeout,
} from 'rxjs/operators';

@Injectable()
export class LMCreateReportDialogService {
  constructor(
    private sohoModalDialogService: SohoModalDialogService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmFormRequestService: LMFormRequestService,
    private lmFieldPickerService: LMFieldPickerService,
    private lmUrlUtilService: LMUrlUtilService,
    private http: HttpClient,
    private lmReportPersonalizationDialogService: LMReportPersonalizationDialogService,
    private lmErrorUtilService: LMErrorUtilService
  ) {}

  // =====================Main=====================
  private _showMainContainer = true;

  // =====================Fields======================
  businessClass: string;
  listName: string;
  userFolder: string;
  lookupUrl: string;
  createReportUrl: string;
  reportType: LMReportType;
  reportName: string;
  isCubeView: boolean;

  private _rawFields: Array<any>;
  private _selectedItems: SohoSwapListItem[] = [];

  openCreateReportDialog(
    mode: LMReportPersonalizationMode,
    reportPlaceholder: any,
    createReportUrl: string,
    actorGroupSelectUrl: string,
    roleSelectUrl: string,
    roleGroupSelectUrl: string,
    reportFieldSearchUrl: string
  ): Observable<LMReportPersonalizationDialogResult> {
    return new Observable<LMReportPersonalizationDialogResult>((observer) => {
      let dialog: LMCreateReportDialogComponent;
      const dialogId = 'CreateReportFromBanner';
      // eslint-disable-next-line prefer-const
      let dialogRef: SohoModalDialogRef<LMCreateReportDialogComponent>;
      const createDialogResult$ =
        new Subject<LMReportPersonalizationDialogResult>();
      const clickContinue$ = new Subject<boolean>();

      const buttons: Array<SohoModalButton> = [
        {
          id: this.lmUniqueIdService.uniqueId([
            dialogId,
            LMDialogButtonKey.CANCEL,
          ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: () => {
            if (this._showMainContainer) {
              createDialogResult$.next({
                status:
                  LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_CANCEL,
              });
              return;
            }

            clickContinue$.next(false);
          },
        },
        {
          id: this.lmUniqueIdService.uniqueId([
            dialogId,
            LMDialogButtonKey.CONTINUE,
          ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CONTINUE),
          isDefault: true,
          click: () => {
            if (this._showMainContainer) {
              // main template
              clickContinue$.next(false);

              if (!dialogRef.componentDialog.isCubeView.value) {
                return;
              }
            }

            if (
              dialogRef.componentDialog.isCubeView.value ||
              this.getDisplayFields()?.length > 0
            ) {
              this.lmReportPersonalizationDialogService
                .runReportPersonalizationDialog(
                  mode,
                  reportPlaceholder,
                  this.reportType,
                  this.businessClass,
                  this.listName,
                  this.userFolder,
                  dialogRef.componentDialog.isCubeView.value
                    ? []
                    : this.getDisplayFields(),
                  createReportUrl,
                  actorGroupSelectUrl,
                  roleSelectUrl,
                  roleGroupSelectUrl,
                  reportFieldSearchUrl,
                  this.reportName,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  true
                )
                .subscribe((result: LMReportPersonalizationDialogResult) => {
                  if (
                    result.status ===
                      LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_SAVED ||
                    result.status ===
                      LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_OK ||
                    result.status ===
                      LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_CANCEL
                  ) {
                    createDialogResult$.next(result);
                  }
                });
            } else {
              this.lmErrorUtilService.handleViewException({
                message: Soho.Locale.translate('NoSelectedFieldsError'),
              });
            }
          },
        },
      ];

      const attributes: SohoAttributesArray = [{ name: 'data-automation-id', value: `lm-create-report-modal` }];

      dialogRef = this.sohoModalDialogService
        .modal(LMCreateReportDialogComponent, null, <SohoModalOptions>{ attributes })
        .title(Soho.Locale.translate('CreateReport'))
        .buttons(buttons)
        .isAlert(false)
        .breakpoint('phablet')
        .apply((createReportDialog: LMCreateReportDialogComponent) => {
          dialog = createReportDialog;
          dialog.url = createReportUrl;
          dialog.clickContinue$ = clickContinue$;
        })
        .open()
        .beforeClose(() => {
          ['ReportFromScratch', 'ReportCopiedFromList'].forEach(
            (type: LMReportType) => {}
          );
          return true;
        })
        .afterClose((result) => {
          createDialogResult$.complete();
          observer.next(result);
          observer.complete();
        });

      createDialogResult$.subscribe(
        (result: LMReportPersonalizationDialogResult) => {
          if (
            result.status ===
              LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_SAVED ||
            result.status ===
              LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_CANCEL
          ) {
            dialogRef.close(result);
          }
        }
      );
    });
  }

  public set showMainContainer(showMainContainer: boolean) {
    this._showMainContainer = showMainContainer;
  }

  //=====================================Fields Template====================================
  public setCreateReportFields(createReport: any) {
    this.businessClass = createReport.businessClass.value;
    this.userFolder = createReport.userFolderId.value;
    this.listName = createReport.listName.value;
    this.reportType = createReport.reportType.value;
    this.reportName = createReport.reportName.value;
    this.isCubeView = createReport.isCubeView.value;
  }

  loadData(searchOptions?: any): Observable<LMCreateReportDisplayField[] | []> {
    if (this.reportType === 'ReportFromScratch') {
      return this.loadDataFromScratch();
    } else if (this.reportType === 'ReportCopiedFromList' || !this.userFolder) {
      return this.loadDataFromList();
    }

    return this.loadDataFromReport();
  }

  private loadDataFromReport(): Observable<LMCreateReportDisplayField[] | []> {
    return this.getFromReportFields(this.userFolder).pipe(
      first(),
      map((displayFields: Array<any>) =>
        displayFields.map((field) => ({
          ...field,
          id: field.index,
          text: field.name,
          value: field.label,
        }))
      )
    );
  }

  private getFromReportFields(userFolderId: string): Observable<any> {
    const formRequest: any = {
      requestType: 'userFolderDisplayFields',
      userFolderId,
    };

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return this.http
      .post(this.reportUrl, JSON.stringify(formRequest), { headers })
      .pipe(
        first(),
        catchError((error) => throwError(error || 'Server error'))
      );
  }

  private get reportUrl(): string {
    const url = `${this.lmUrlUtilService.getBaseUrl()}/report/${
      this.businessClass
    }`;
    return this.lmUrlUtilService.propagateSessionKeys(url, '?');
  }

  private loadDataFromList(): Observable<LMCreateReportDisplayField[] | []> {
    return this.lmFormRequestService
      .getDisplayFields(
        this.lmUrlUtilService.getReportUrl(this.businessClass),
        this.businessClass,
        this.listName
      )
      .pipe(
        first(),
        map((displayFields: Array<any>) =>
          displayFields.map((field) => ({
            ...field,
            id: field.index,
            text: field.name,
            value: field.label,
          }))
        )
      );
  }
  
  public getPrimaryList(businessClass: string): Observable<any> {
    return this.lmFormRequestService.getPrimaryList(
      this.lmUrlUtilService.getReportUrl(this.businessClass),
      FormRequestType.PrimaryList,
      businessClass
    )
    .pipe(first());
  }

  public getModules(
    businessClass: string,
    listName: string
  ): Observable<{ modules: LMAppReport['modules'] }> {
    return this.lmFormRequestService
      .getModules(
        this.lmUrlUtilService.getReportUrl(businessClass),
        businessClass,
        listName
      )
      .pipe(first());
  }

  public getCategoriesByModule(
    businessClass: string,
    listName: string
  ): Observable<{ categoriesByModule: LMAppReport['categoriesByModule'] }> {
    return this.lmFormRequestService
      .getCategoriesModule(
        this.lmUrlUtilService.getReportUrl(businessClass),
        businessClass,
        listName
      )
      .pipe(first());
  }

  public getWebApps(
    businessClass: string,
    listName: string
  ): Observable<{ webApps: LMAppReport['webApps'] }> {
    return this.lmFormRequestService
      .getWebApps(
        this.lmUrlUtilService.getReportUrl(businessClass),
        businessClass,
        listName
      )
      .pipe(first());
  }

  private loadDataFromScratch(): Observable<LMCreateReportDisplayField[] | []> {
    this.lookupUrl = `${this.lmUrlUtilService.getBaseUrl()}/list/${
      this.businessClass
    }`;

    const params: LMFieldPickerRequest = {
      requestType: ListRequestType.FieldSpecForBusClass,
      businessClass: this.businessClass,
      view: 'Fields',
      includeAuditFields: false,
      searchScope: 'normal',
      relatedFieldFilterComparator: 'equals',
      relatedFieldFilter: '',
    };

    return this.lmFieldPickerService
      .getFieldSpecData(params, this.lookupUrl, this.businessClass)
      .pipe(
        first(),
        map((response: LMFieldPickerResponse) => {
          if (response?.data.length > 0) {
            return response.data.map((field, index) => ({
              ...field,
              id: index,
              name: field?.fieldName,
              text: field?.fieldName,
              value: field.label,
            }));
          }
          return [];
        })
      );
  }

  private getDisplayFields() {
    const selectedItems = this.selectedItems.map((item) => item.text) || [];
    const selectedFields = this.rawFields?.filter((item) => {
      if (selectedItems.includes(item.name)) {
        return item;
      }
    });

    if (this.reportType !== 'ReportFromScratch') {
      return selectedFields;
    }

    return selectedFields.map((field) => ({
      name: field.name,
      label: field.label,
      canUpdate: field.canUpdate,
      type: 'Field',
      canTotal: field.canTotal,
      added: true,
      index: Date.now(),
      allowUpdate: false,
    }));
  }

  get rawFields() {
    return this._rawFields;
  }

  set rawFields(fields: Array<any>) {
    this._rawFields = fields;
  }

  get selectedItems() {
    return this._selectedItems;
  }

  set selectedItems(selectedItems: SohoSwapListItem[]) {
    this._selectedItems = selectedItems;

    if (this.reportType === 'ReportFromScratch') {
      const selectedFields = this._selectedItems.map((field) => field.text);

      this.lmFormRequestService
        .validateFields(
          this.lmUrlUtilService.getReportUrl(this.businessClass),
          this.businessClass,
          this.listName,
          selectedFields
        )
        .pipe(first())
        .subscribe((displayFieldsSpec) =>
          displayFieldsSpec.map((displayFieldSpec) => {
            this._rawFields.find((field) => {
              if (field.name === displayFieldSpec.name) {
                field.canTotal = displayFieldSpec.canTotal;
                field.canUpdate = displayFieldSpec.canUpdate;
              }
            });
          })
        );
    }
  }

  public static pipeOperatorHelper(component: any) {
    return pipe(
      takeUntil(component?.unsubscribe$),
      take(1),
      timeout(5 * 1000),
      catchError((error: any): Observable<any> => {
        if (error.name === 'TimeoutError') {
          return of(undefined);
        }
        return of(component.lmErrorUtilService.handleError(error));
      })
    );
  }
}
